import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Shield, TrendingUp, Package, Megaphone,
  FolderKanban, Wrench, UserCheck, AlertTriangle, CheckCircle,
  Clock, DollarSign, Truck, Store, BarChart3, Activity,
  RefreshCw, ChevronRight, ArrowUpRight, ArrowDownRight,
  Calendar, Bell, Target, Award, Zap, Eye, AlertCircle,
  ShoppingCart, FileText, Briefcase, PieChart, Loader2
} from 'lucide-react';
import CalendarWidget from '../components/widgets/CalendarWidget';
import RemindersWidget from '../components/widgets/RemindersWidget';
import MeetingsWidget from '../components/widgets/MeetingsWidget';
import MDQuickApprovalsWidget from '../components/widgets/MDQuickApprovalsWidget';
import MyTasksWidget from '../components/widgets/MyTasksWidget';
import TasksIAssignedWidget from '../components/widgets/TasksIAssignedWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section configurations with role restrictions
// Only CEO and GM can see operations sections
const ALL_SECTIONS = [
  { id: 'stores', label: 'Stores', icon: Store, color: 'bg-blue-500', roles: ['ceo', 'md', 'gm'] },
  { id: 'warehouse', label: 'Warehouse', icon: Package, color: 'bg-orange-500', roles: ['ceo', 'md', 'gm', 'warehouse_admin', 'warehouse_manager'] },
  { id: 'compliance', label: 'Compliance', icon: Shield, color: 'bg-green-500', roles: ['ceo', 'md', 'gm'] },
  { id: 'sales', label: 'Sales', icon: DollarSign, color: 'bg-emerald-500', roles: ['ceo', 'md', 'gm'] },
  { id: 'purchase', label: 'Purchase', icon: ShoppingCart, color: 'bg-purple-500', roles: ['ceo', 'md', 'gm'] },
  { id: 'marketing', label: 'Marketing', icon: Megaphone, color: 'bg-pink-500', roles: ['ceo', 'md', 'gm'] },
  { id: 'projects', label: 'Projects', icon: FolderKanban, color: 'bg-indigo-500', roles: ['ceo', 'md', 'gm', 'project_manager'] },
  { id: 'tech', label: 'Tech', icon: Wrench, color: 'bg-slate-500', roles: ['ceo', 'md', 'gm', 'tech_support'] },
  { id: 'hr', label: 'HR', icon: UserCheck, color: 'bg-red-500', roles: ['ceo', 'md', 'gm', 'hr', 'hr_manager'] },
];

export default function OperationsCommandCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({});
  const [activeSection, setActiveSection] = useState('overview');
  
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const endpoints = [
        { key: 'occ', url: '/api/occ/dashboard' },
        { key: 'compliance', url: '/api/compliance/dashboard' },
        { key: 'hr', url: '/api/hr-dashboard/summary' },
        { key: 'projects', url: '/api/pm/dashboard' },
        { key: 'shifts', url: '/api/shifts/summary' },
      ];

      const results = await Promise.allSettled(
        endpoints.map(ep => 
          fetch(`${API_URL}${ep.url}`, { headers }).then(r => r.ok ? r.json() : null)
        )
      );

      const newData = {};
      endpoints.forEach((ep, idx) => {
        if (results[idx].status === 'fulfilled' && results[idx].value) {
          newData[ep.key] = results[idx].value;
        }
      });

      setData(newData);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
    toast.success('Dashboard refreshed');
  };

  // Filter sections based on user role
  const userRole = user?.role || '';
  const SECTIONS = ALL_SECTIONS.filter(section => 
    section.roles.includes(userRole)
  );

  // Calculate health scores
  const getHealthScore = (section) => {
    switch (section) {
      case 'stores':
        return data.occ?.store_metrics?.overall_health || 85;
      case 'warehouse':
        return data.occ?.warehouse_metrics?.efficiency || 78;
      case 'compliance':
        return data.compliance?.overall_compliance || 92;
      case 'hr':
        return data.hr?.attendance_rate || 95;
      case 'projects':
        return data.projects?.on_track_percentage || 80;
      default:
        return 85;
    }
  };

  const getHealthColor = (score) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getHealthBg = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#DC2626] mx-auto mb-4" />
          <p className="text-gray-500">Loading Operations Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="operations-command-center">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#ffffff' }}>My Dashboard</h1>
              <p className="text-gray-300 mt-1">Real-time overview of all business operations</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <Activity className="w-3 h-3 mr-1" />
                Live
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 mt-6">
            {SECTIONS.map(section => {
              const score = getHealthScore(section.id);
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`p-3 rounded-xl transition-all ${
                    activeSection === section.id
                      ? 'bg-white/20 ring-2 ring-white/40'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-lg ${section.color}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-xs font-medium">{section.label}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-lg font-bold ${getHealthColor(score)}`}>{score}%</span>
                    {score >= 90 ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : score >= 70 ? (
                      <AlertCircle className="w-4 h-4 text-yellow-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            {/* Alerts & Actions - Role-based visibility */}
            <div className={`grid grid-cols-1 gap-6 ${
              ['ceo', 'md', 'gm', 'hr', 'hr_manager', 'operations_manager'].includes(userRole) 
                ? 'lg:grid-cols-3' 
                : 'lg:grid-cols-1'
            }`}>
              {/* Critical Alerts - Visible to all */}
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Critical Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.occ?.alerts?.critical?.length > 0 ? (
                      data.occ.alerts.critical.slice(0, 3).map((alert, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-red-50 rounded-lg">
                          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span>{alert.message || alert}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-sm">No critical alerts</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Pending Approvals - HR/Management only */}
              {['ceo', 'md', 'gm', 'hr', 'hr_manager', 'operations_manager'].includes(userRole) && (
                <Card className="border-l-4 border-l-yellow-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-500" />
                      Pending Approvals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm">Leave Requests</span>
                        <Badge variant="secondary">{data.hr?.pending_leaves || 0}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm">Shift Swaps</span>
                        <Badge variant="secondary">{data.shifts?.pending_swaps || 0}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm">Expenses</span>
                        <Badge variant="secondary">{data.occ?.pending_expenses || 0}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Today's Summary - HR/Management only */}
              {['ceo', 'md', 'gm', 'hr', 'hr_manager', 'operations_manager'].includes(userRole) && (
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Today's Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm">Staff On Duty</span>
                        <span className="font-bold">{data.hr?.on_duty || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm">Stores Open</span>
                        <span className="font-bold">{data.occ?.stores_open || 5}</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm">Active Deliveries</span>
                        <span className="font-bold">{data.occ?.active_deliveries || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Quick Approvals (MD/CEO/GM only) */}
            {['md', 'ceo', 'gm'].includes(userRole) && (
              <div className="grid grid-cols-1 gap-6">
                <MDQuickApprovalsWidget />
              </div>
            )}

            {/* Task Management Widgets */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <MyTasksWidget expandable={true} />
              <CalendarWidget expandable={true} />
              <TasksIAssignedWidget expandable={true} />
            </div>

            {/* Meetings & Reminders Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MeetingsWidget />
              <RemindersWidget />
            </div>

            {/* Section Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SECTIONS.map(section => (
                <SectionCard
                  key={section.id}
                  section={section}
                  data={data}
                  score={getHealthScore(section.id)}
                  onClick={() => setActiveSection(section.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Stores Section */}
        {activeSection === 'stores' && (
          <SectionDetail
            title="Store Operations"
            icon={Store}
            color="blue"
            metrics={[
              { label: 'Total Stores', value: data.occ?.total_stores || 5, icon: Building2 },
              { label: 'Open Now', value: data.occ?.stores_open || 5, icon: CheckCircle, color: 'green' },
              { label: 'Avg. Rating', value: `${data.occ?.avg_rating || 4.5}/5`, icon: Award },
              { label: 'Daily Sales', value: `$${(data.occ?.daily_sales || 0).toLocaleString()}`, icon: DollarSign },
            ]}
            items={[
              { label: 'Hoppers Crossing', status: 'open', health: 95 },
              { label: 'Melton', status: 'open', health: 88 },
              { label: 'Craigieburn', status: 'open', health: 92 },
              { label: 'Clyde', status: 'open', health: 85 },
              { label: 'Auburn', status: 'open', health: 90 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'Store Inspections', path: '/admin/inspections' },
              { label: 'Store Checklists', path: '/admin/store-checklists' },
              { label: 'Store Performance', path: '/admin/reports/store-performance' },
            ]}
          />
        )}

        {/* Warehouse Section */}
        {activeSection === 'warehouse' && (
          <SectionDetail
            title="Warehouse Operations"
            icon={Package}
            color="orange"
            metrics={[
              { label: 'Inventory Items', value: data.occ?.inventory_items || '2,450', icon: Package },
              { label: 'Pending Orders', value: data.occ?.pending_orders || 12, icon: Clock },
              { label: 'Active Drivers', value: data.occ?.active_drivers || 3, icon: Truck },
              { label: 'Deliveries Today', value: data.occ?.deliveries_today || 8, icon: CheckCircle },
            ]}
            items={[
              { label: 'Receiving', status: 'active', health: 95 },
              { label: 'Dispatch', status: 'active', health: 88 },
              { label: 'Stock Levels', status: 'good', health: 82 },
              { label: 'Driver Fleet', status: 'active', health: 90 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'Driver Tracking', path: '/admin/driver-tracking' },
              { label: 'Delivery Reports', path: '/admin/delivery-reports' },
            ]}
          />
        )}

        {/* Compliance Section */}
        {activeSection === 'compliance' && (
          <SectionDetail
            title="Compliance & Safety"
            icon={Shield}
            color="green"
            metrics={[
              { label: 'Overall Score', value: `${data.compliance?.overall_compliance || 92}%`, icon: Shield },
              { label: 'Expiring Soon', value: data.compliance?.expiring_soon || 3, icon: AlertTriangle, color: 'yellow' },
              { label: 'Overdue Items', value: data.compliance?.overdue || 1, icon: AlertCircle, color: 'red' },
              { label: 'Inspections Done', value: data.compliance?.inspections_completed || 45, icon: CheckCircle },
            ]}
            items={[
              { label: 'Food Safety', status: 'compliant', health: 98 },
              { label: 'OH&S', status: 'compliant', health: 95 },
              { label: 'Licenses', status: 'review', health: 85 },
              { label: 'Training', status: 'compliant', health: 90 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'Compliance Dashboard', path: '/admin/compliance' },
              { label: 'Expiry Tracker', path: '/admin/compliance-expiry' },
              { label: 'Inspections', path: '/admin/inspections' },
            ]}
          />
        )}

        {/* Sales Section */}
        {activeSection === 'sales' && (
          <SectionDetail
            title="Sales Performance"
            icon={DollarSign}
            color="emerald"
            metrics={[
              { label: 'Today Sales', value: `$${(data.occ?.today_sales || 15420).toLocaleString()}`, icon: DollarSign },
              { label: 'MTD Sales', value: `$${(data.occ?.mtd_sales || 325000).toLocaleString()}`, icon: TrendingUp },
              { label: 'Transactions', value: data.occ?.transactions || 245, icon: FileText },
              { label: 'Avg. Basket', value: `$${data.occ?.avg_basket || 63}`, icon: ShoppingCart },
            ]}
            items={[
              { label: 'Hoppers Crossing', value: '$4,250', trend: '+12%' },
              { label: 'Melton', value: '$3,890', trend: '+8%' },
              { label: 'Craigieburn', value: '$3,420', trend: '+5%' },
              { label: 'Clyde', value: '$2,180', trend: '-2%' },
              { label: 'Auburn', value: '$1,680', trend: '+15%' },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'Sales Dashboard', path: '/admin/sales' },
              { label: 'Analytics', path: '/admin/analytics' },
            ]}
          />
        )}

        {/* Purchase Section */}
        {activeSection === 'purchase' && (
          <SectionDetail
            title="Purchase & Inventory"
            icon={ShoppingCart}
            color="purple"
            metrics={[
              { label: 'Open POs', value: data.occ?.open_pos || 8, icon: FileText },
              { label: 'Pending Receipts', value: data.occ?.pending_receipts || 5, icon: Clock },
              { label: 'Low Stock Items', value: data.occ?.low_stock || 12, icon: AlertTriangle, color: 'yellow' },
              { label: 'MTD Spend', value: `$${(data.occ?.mtd_spend || 85000).toLocaleString()}`, icon: DollarSign },
            ]}
            items={[
              { label: 'Grocery Supplies', status: 'ordered', health: 90 },
              { label: 'Dairy Products', status: 'low', health: 65 },
              { label: 'Frozen Items', status: 'good', health: 85 },
              { label: 'Beverages', status: 'good', health: 92 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[]}
          />
        )}

        {/* Marketing Section */}
        {activeSection === 'marketing' && (
          <SectionDetail
            title="Marketing & Campaigns"
            icon={Megaphone}
            color="pink"
            metrics={[
              { label: 'Active Campaigns', value: data.occ?.active_campaigns || 3, icon: Megaphone },
              { label: 'Social Reach', value: `${(data.occ?.social_reach || 12500).toLocaleString()}`, icon: Users },
              { label: 'Email Open Rate', value: `${data.occ?.email_rate || 28}%`, icon: BarChart3 },
              { label: 'Store Promos', value: data.occ?.store_promos || 5, icon: Award },
            ]}
            items={[
              { label: 'Diwali Sale', status: 'active', health: 95 },
              { label: 'Weekly Specials', status: 'active', health: 88 },
              { label: 'New Store Opening', status: 'planned', health: 100 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'Marketing Dashboard', path: '/admin/sales-marketing' },
            ]}
          />
        )}

        {/* Projects Section */}
        {activeSection === 'projects' && (
          <SectionDetail
            title="Projects & Expansion"
            icon={FolderKanban}
            color="indigo"
            metrics={[
              { label: 'Active Projects', value: data.projects?.total || 4, icon: FolderKanban },
              { label: 'On Track', value: data.projects?.on_track || 3, icon: CheckCircle, color: 'green' },
              { label: 'At Risk', value: data.projects?.at_risk || 1, icon: AlertTriangle, color: 'yellow' },
              { label: 'Open Tasks', value: data.projects?.open_tasks || 24, icon: Target },
            ]}
            items={data.projects?.projects?.slice(0, 4).map(p => ({
              label: p.name,
              status: p.status,
              health: p.progress || 50
            })) || [
              { label: 'Auburn Store Setup', status: 'in_progress', health: 75 },
              { label: 'Warehouse Expansion', status: 'planning', health: 30 },
              { label: 'POS Upgrade', status: 'in_progress', health: 60 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'Project Dashboard', path: '/admin/project-manager' },
              { label: 'All Projects', path: '/admin/projects' },
            ]}
          />
        )}

        {/* Tech Section */}
        {activeSection === 'tech' && (
          <SectionDetail
            title="Technology & Systems"
            icon={Wrench}
            color="slate"
            metrics={[
              { label: 'System Uptime', value: '99.9%', icon: Activity },
              { label: 'Open Tickets', value: data.occ?.open_tickets || 5, icon: FileText },
              { label: 'POS Systems', value: `${data.occ?.pos_online || 5}/5`, icon: CheckCircle },
              { label: 'Last Backup', value: '2h ago', icon: Clock },
            ]}
            items={[
              { label: 'POS Systems', status: 'online', health: 100 },
              { label: 'CCTV Network', status: 'online', health: 98 },
              { label: 'Payment Gateway', status: 'online', health: 100 },
              { label: 'Inventory System', status: 'online', health: 95 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[]}
          />
        )}

        {/* HR Section */}
        {activeSection === 'hr' && (
          <SectionDetail
            title="Human Resources"
            icon={UserCheck}
            color="red"
            metrics={[
              { label: 'Total Staff', value: data.hr?.total_staff || 51, icon: Users },
              { label: 'On Duty', value: data.hr?.on_duty || 28, icon: CheckCircle, color: 'green' },
              { label: 'On Leave', value: data.hr?.on_leave || 3, icon: Calendar },
              { label: 'Attendance', value: `${data.hr?.attendance_rate || 95}%`, icon: Target },
            ]}
            items={[
              { label: 'Attendance', status: 'good', health: 95 },
              { label: 'Training', status: 'good', health: 88 },
              { label: 'Onboarding', status: 'active', health: 90 },
              { label: 'Performance', status: 'review', health: 82 },
            ]}
            onBack={() => setActiveSection('overview')}
            navigate={navigate}
            links={[
              { label: 'HR Dashboard', path: '/admin/hr' },
              { label: 'Onboarding', path: '/admin/onboarding-wizard' },
              { label: 'Team Structure', path: '/admin/team-structure' },
              { label: 'Org Chart', path: '/admin/org-chart' },
            ]}
          />
        )}
      </div>
    </div>
  );
}

// Section Card Component
function SectionCard({ section, data, score, onClick }) {
  const Icon = section.icon;
  
  const getStatusColor = (score) => {
    if (score >= 90) return 'border-green-500 bg-green-50';
    if (score >= 70) return 'border-yellow-500 bg-yellow-50';
    return 'border-red-500 bg-red-50';
  };

  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all border-l-4 ${getStatusColor(score)}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${section.color}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">{section.label}</h3>
              <p className="text-sm text-gray-500">Health Score</p>
            </div>
          </div>
          <div className="text-right">
            <span className={`text-2xl font-bold ${
              score >= 90 ? 'text-green-600' : score >= 70 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {score}%
            </span>
          </div>
        </div>
        <Progress 
          value={score} 
          className="mt-3 h-2"
        />
        <div className="flex items-center justify-end mt-2 text-sm text-gray-500">
          <span>View Details</span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </div>
      </CardContent>
    </Card>
  );
}

// Section Detail Component
function SectionDetail({ title, icon: Icon, color, metrics, items, onBack, navigate, links }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500',
    emerald: 'bg-emerald-500',
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
    indigo: 'bg-indigo-500',
    slate: 'bg-slate-500',
    red: 'bg-red-500',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack}>
            <ChevronRight className="w-5 h-5 rotate-180" />
          </Button>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold">{title}</h2>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((metric, idx) => {
          const MetricIcon = metric.icon;
          return (
            <Card key={idx}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MetricIcon className={`w-5 h-5 ${
                    metric.color === 'green' ? 'text-green-500' :
                    metric.color === 'yellow' ? 'text-yellow-500' :
                    metric.color === 'red' ? 'text-red-500' :
                    'text-gray-500'
                  }`} />
                  <span className="text-sm text-gray-500">{metric.label}</span>
                </div>
                <p className="text-2xl font-bold">{metric.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Items */}
      <Card>
        <CardHeader>
          <CardTitle>Status Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{item.label}</span>
                  {item.status && (
                    <Badge className={
                      item.status === 'open' || item.status === 'active' || item.status === 'online' || item.status === 'good' || item.status === 'compliant'
                        ? 'bg-green-100 text-green-700'
                        : item.status === 'review' || item.status === 'planned' || item.status === 'low' || item.status === 'ordered'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }>
                      {item.status}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {item.value && <span className="font-bold">{item.value}</span>}
                  {item.trend && (
                    <span className={`text-sm flex items-center ${
                      item.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {item.trend.startsWith('+') ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4" />
                      )}
                      {item.trend}
                    </span>
                  )}
                  {item.health !== undefined && (
                    <div className="flex items-center gap-2">
                      <Progress value={item.health} className="w-24 h-2" />
                      <span className={`text-sm font-medium ${
                        item.health >= 90 ? 'text-green-600' :
                        item.health >= 70 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {item.health}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      {links && links.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Quick Links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {links.map((link, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
